<template>
  <div>
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>
          Indstillinger
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          :loading="saving"
          :disabled="!valid"
          @click="save"
        >
          {{ $t('common.save') }}
          <v-icon right>
            mdi-content-save-outline
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form
          ref="settings"
          v-model="valid"
        >
          <v-row>
            <v-col
              cols="12"
              md="8"
            >
              <v-text-field
                v-model="customer.title"
                label="Navn"
                outlined
                :rules="[value => !!value || 'Skal udfyldes']"
              />
              <v-text-field
                v-model="customer.initials"
                label="Initialer"
                outlined
                :rules="[value => !!value || 'Skal udfyldes', value => String(value).length <= 3 || 'Maks. 3 tegn']"
              />
              <v-select
                v-model="customer.defaultLocale"
                :items="$store.state.availableLocales"
                label="Default sprog"
                outlined
              />
              <v-select
                v-model="customer.dashboardChartGrouping"
                :items="dashboardChartGroupings"
                label="Gruppering på Dashboard"
                outlined
              />
              <v-autocomplete
                v-model="customer.supportTicketDefaultAssignedTo_Person_Id"
                :items="internalUsers"
                :loading="loading.internalUsers"
                label="Support ansvarlig"
                hint="Nye supportsager bliver automatisk tildelt denne person"
                item-text="fullName"
                item-value="id"
                persistent-hint
                outlined
              />
            </v-col>
            <v-col>
              <label for="logo-input" class="custom-logo-input">
                <header class="d-flex align-center mb-2" style="height: 12px;">
                  <span>
                    Logo
                  </span>
                  <v-fade-transition>
                    <div v-if="customer.logoBase64" class="ml-auto">
                      <v-btn color="error" icon @click="customer.logoBase64 = null">
                        <v-icon>
                          mdi-delete-outline
                        </v-icon>
                      </v-btn>
                    </div>
                  </v-fade-transition>
                </header>
                <v-btn @click="$refs.logoInput.click()">
                  <img
                    v-if="customer.logoBase64"
                    :src="customer.logoBase64"
                    style="max-width: 400px; max-height: 400px; object-fit: contain;"
                  >
                  <v-icon v-else x-large>
                    mdi-camera-outline
                  </v-icon>
                </v-btn>
              </label>
              <input type="file" ref="logoInput" style="display: none;" @change="onLogoChange">
            </v-col>
          </v-row>

          <v-btn small @click="showAdvanced = !showAdvanced">
            Avanceret
            <v-icon right>mdi-lock-outline</v-icon>
          </v-btn>
          
          <v-slide-y-transition>
            <div v-if="showAdvanced" class="mt-6">
              <v-alert class="mb-5" type="warning" text dense>
                Halv gas! Ved du hvad du laver?
              </v-alert>
              <v-select
                v-model="customer.codeFormat"
                :items="['LocationBased', 'TypeBased']"
                label="Kodeformat"
                outlined
                :rules="[value => !!value || 'Skal udfyldes']"
              />
              <v-switch
                v-model="customer.enableTaskSync"
                label="Aktivér opgavesynkronisering"
                hint="Kræver integration - spørg udviklerne hvis du er i tvivl"
                persistent-hint
              />

              <div class="mt-6">
                ERP konfiguration
              </div>
              <prism-editor
                v-model="customer.erpConfigurationJson"
                :highlight="highlighter"
                line-numbers
                class="config-editor"
              />
            </div>
          </v-slide-y-transition>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { PrismEditor } from 'vue-prism-editor';
import 'vue-prism-editor/dist/prismeditor.min.css';
import { highlight, languages } from 'prismjs/components/prism-core';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism-tomorrow.css';

export default {
  name: 'CustomerSettings',
  components: { PrismEditor },
  props: ['customer'],
  data() {
    return {
      valid: false,
      saving: false,
      showAdvanced: false,
      logo: null,
      internalUsers: [],
      loading: {
        internalUsers: true
      },
      dashboardChartGroupings: [
        {
          text: this.$tc('common.department', 2),
          value: 'Department'
        },
        {
          text: this.$tc('common.taskType', 2),
          value: 'TaskType'
        },
        {
          text: this.$tc('common.subjectArea', 2),
          value: 'SubjectArea'
        }
      ]
    }
  },
  mounted() {
    this.getInternalUsers();
  },
  methods: {
    save: async function() {
      this.saving = true;

      try {
        await this.$api.patch(`customers/${this.$route.params.customerId}`, this.customer);

        this.$emit('update');
        this.$notifier.showSuccess({ message: 'Ændringer blev gemt' })
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    highlighter(code) {
      return highlight(code, languages.json);
    },
    getInternalUsers: async function() {
      this.loading.internalUsers = true;

      try {
        const { data } = await this.$api.get('users', {
          params: {
            organizationKinds: ['internal']
          }
        });

        this.internalUsers = data;
      } catch (error) {
        this.$notifier.showError({});
      } finally {
        this.loading.internalUsers = false;
      }
    },
    onLogoChange: function(event) {
      const files = event.target.files;

      if (files?.length) {
        const file = files[0];
        const reader = new FileReader();

        reader.addEventListener('load', () => {
          this.customer.logoBase64 = reader.result;
        })

        if (file) {
          reader.readAsDataURL(file);
        }
      }
    }
  }
}
</script>

<style>
  .config-editor {
    background: #2d2d2d;
    color: #ccc;

    /* you must provide font-family font-size line-height. Example: */
    font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
    font-size: 14px;
    line-height: 1.5;
    padding: 5px;
  }

  .prism-editor__textarea:focus {
    outline: none;
  }

  .custom-logo-input .v-btn {
    margin-top: 6px;
    height: calc(70% - 6px) !important;
    width: 100%;
  }
</style>